// Reusable primitives ported 1:1 from design-handoff/src/primitives.jsx.
// Inline-styled to stay faithful to the handoff's visual decisions.

import type { CSSProperties, ReactNode } from "react";
import { PlatformIcon, Rosette } from "./icons";
import type { Platform } from "@/lib/types";

// ──────────────────────────────────────────────────────────────────────────────
// "Disable comments" capability — which platforms can ACTUALLY honour it.
//
// Verified live against each platform API on 2026-08-06:
//   x          POST /2/tweets reply_settings=mentionedUsers  (works)
//   instagram  POST /{ig-media-id} comment_enabled=false     (works)
//   facebook   no comment-control parameter exists at all
//   linkedin   /rest/socialMetadata commentsState=CLOSED exists, but our
//              app is 403 ACCESS_DENIED (needs Community Management scopes)
//   telegram   channel comments come from the linked discussion group;
//              there is no per-message control in the Bot API
//
// The backend stores the flag for every platform, but only acts on the
// two below. The UI must never imply otherwise — hence the honest hint.
// ──────────────────────────────────────────────────────────────────────────────

export const COMMENTS_DISABLE_SUPPORTED: Platform[] = ["x", "instagram"];

const COMMENTS_PLATFORM_LABEL: Record<Platform, string> = {
  x: "X",
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

function joinList(items: string[], tr: boolean): string {
  if (items.length <= 1) return items[0] ?? "";
  const last = items[items.length - 1];
  return items.slice(0, -1).join(", ") + (tr ? " ve " : " and ") + last;
}

/**
 * commentsDisabledHint — the honest, selection-aware caption under the
 * "Disable comments" toggle. Names exactly which of the CURRENTLY SELECTED
 * platforms will honour the flag and which will silently ignore it, so the
 * operator is never told a post went out with comments off when it did not.
 */
export function commentsDisabledHint(selected: Platform[], tr: boolean): string {
  const honours = selected.filter((p) => COMMENTS_DISABLE_SUPPORTED.includes(p));
  const ignores = selected.filter((p) => !COMMENTS_DISABLE_SUPPORTED.includes(p));

  if (selected.length === 0) {
    return tr
      ? "Yalnızca X ve Instagram destekliyor. Facebook, LinkedIn ve Telegram API üzerinden yorum kapatmayı desteklemiyor."
      : "Only X and Instagram support this. Facebook, LinkedIn and Telegram can't turn off comments via their API.";
  }
  const hon = joinList(honours.map((p) => COMMENTS_PLATFORM_LABEL[p]), tr);
  const ign = joinList(ignores.map((p) => COMMENTS_PLATFORM_LABEL[p]), tr);

  if (honours.length === 0) {
    return tr
      ? `Seçili platformların hiçbiri bunu desteklemiyor — ${ign} API üzerinden yorum kapatmayı desteklemiyor.`
      : `None of the selected platforms support this — ${ign} can't turn off comments via their API.`;
  }
  if (ignores.length === 0) {
    return tr ? `${hon} için geçerli.` : `Applies to ${hon}.`;
  }
  return tr
    ? `${hon} için geçerli — ${ign} API üzerinden yorum kapatmayı desteklemiyor.`
    : `Applies to ${hon} — ${ign} can't turn off comments via their API.`;
}

// ──────────────────────────────────────────────────────────────────────────────
// Badge — colour-toned status pill. Status maps to a colour family.
// ──────────────────────────────────────────────────────────────────────────────

const BADGE_CLASS: Record<string, string> = {
  ok: "badge-green",
  success: "badge-green",
  pending: "badge-amber",
  warning: "badge-amber",
  failed: "badge-red",
  error: "badge-red",
  info: "badge-blue",
  draft: "badge-gray",
  scheduled: "badge-blue",
  published: "badge-green",
  publishing: "badge-purple",
  cancelled: "badge-gray",
  neutral: "badge-gray",
};

export function Badge({ status = "neutral", label }: { status?: string; label?: string }) {
  const cls = BADGE_CLASS[status] ?? "badge-gray";
  return (
    <span className={`badge ${cls}`}>
      <span className="dot" />
      {label ?? status}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Avatar — initials circle, used for users.
// ──────────────────────────────────────────────────────────────────────────────

export function Avatar({
  name,
  color = "#E02D1C",
  size = 28,
  ring = false,
}: {
  name?: string;
  color?: string;
  size?: number;
  ring?: boolean;
}) {
  const initials = name
    ? name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: size * 0.4,
        letterSpacing: "-0.02em",
        boxShadow: ring ? `0 0 0 2px var(--bg), 0 0 0 3px ${color}` : "none",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// BrandMark — squared brand square with initials. Adapts when `mono` is set.
// ──────────────────────────────────────────────────────────────────────────────

type BrandLike = {
  trade_name?: string;
  name?: string;
  mark?: string;
  accent_hex?: string;
  accent?: string;
  accentSoft?: string;
  accent_soft_hex?: string;
};

export function BrandMark({
  brand,
  size = 28,
  mono = false,
}: {
  brand: BrandLike;
  size?: number;
  mono?: boolean;
}) {
  const accent = brand.accent_hex ?? brand.accent ?? "#E02D1C";
  const accentSoft = brand.accent_soft_hex ?? brand.accentSoft ?? hexToRgba(accent, 0.12);
  const color = mono ? "#fff" : accent;
  const text = brand.mark ?? initialsOf(brand.trade_name ?? brand.name);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: mono ? "var(--elev)" : accentSoft,
        color,
        border: `1px solid ${mono ? "var(--b2)" : hexToRgba(accent, 0.33)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: size * 0.4,
        letterSpacing: "-0.02em",
        flexShrink: 0,
      }}
    >
      {text}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PlatformChip — square tinted with the platform's brand colour.
// ──────────────────────────────────────────────────────────────────────────────

const PLATFORM_META: Record<string, { name: string; color: string }> = {
  instagram: { name: "Instagram", color: "#E1306C" },
  facebook: { name: "Facebook", color: "#1877F2" },
  x: { name: "X", color: "#FFFFFF" },
  linkedin: { name: "LinkedIn", color: "#0A66C2" },
  telegram: { name: "Telegram", color: "#26A5E4" },
};

export function PlatformChip({
  platform,
  size = 22,
  active = true,
}: {
  platform: string;
  size?: number;
  active?: boolean;
}) {
  const meta = PLATFORM_META[platform] ?? { name: platform, color: "#888" };
  return (
    <div
      title={meta.name}
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: active ? hexToRgba(meta.color, 0.13) : "var(--elev)",
        color: active ? meta.color : "var(--mu)",
        border: `1px solid ${active ? hexToRgba(meta.color, 0.33) : "var(--b2)"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <PlatformIcon platform={platform} size={size * 0.55} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Tabs — underline-style. Active tab gets accent border.
// ──────────────────────────────────────────────────────────────────────────────

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
}: {
  value: T;
  onChange: (id: T) => void;
  tabs: { id: T; label: string; icon?: ReactNode; count?: number }[];
}) {
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--b1)" }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: "10px 14px",
            fontSize: 13,
            fontWeight: 500,
            color: value === t.id ? "var(--tx)" : "var(--tx2)",
            borderBottom: `2px solid ${value === t.id ? "var(--accent)" : "transparent"}`,
            marginBottom: -1,
            transition: "color 150ms, border-color 150ms",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {t.icon}
          {t.label}
          {t.count != null ? (
            <span className="mono" style={{ fontSize: 10, color: "var(--mu)", marginLeft: 2 }}>
              {t.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Skeleton — shimmer placeholder. Pair with .skel class from tokens.css.
// ──────────────────────────────────────────────────────────────────────────────

export function Skeleton({ w = "100%", h = 12, style }: { w?: string | number; h?: number; style?: CSSProperties }) {
  return <div className="skel" style={{ width: w, height: h, ...style }} />;
}

// ──────────────────────────────────────────────────────────────────────────────
// KPI — big number card with eyebrow + delta arrow + sub.
// ──────────────────────────────────────────────────────────────────────────────

export function KPI({
  label,
  value,
  delta,
  deltaKind = "up",
  sub,
  onClick,
}: {
  label: string;
  value: string | number;
  delta?: string;
  deltaKind?: "up" | "down";
  sub?: string;
  onClick?: () => void;
}) {
  const clickable = Boolean(onClick);
  return (
    <div
      className="card"
      style={{
        padding: 20,
        cursor: clickable ? "pointer" : "default",
        transition: clickable ? "transform 0.12s ease, border-color 0.12s ease" : undefined,
      }}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      onMouseEnter={
        clickable
          ? (e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "var(--b2)";
            }
          : undefined
      }
      onMouseLeave={
        clickable
          ? (e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "";
            }
          : undefined
      }
    >
      <div className="eyebrow neutral" style={{ marginBottom: 12 }}>— {label}</div>
      <div className="t-num" style={{ fontSize: 34, lineHeight: 1, color: "var(--tx)" }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 12 }}>
        {delta != null ? (
          <span
            style={{
              color: deltaKind === "up" ? "var(--success)" : "var(--error)",
              fontFamily: "var(--mono)",
              fontWeight: 600,
            }}
          >
            {deltaKind === "up" ? "↑" : "↓"} {delta}
          </span>
        ) : null}
        {sub ? <span style={{ color: "var(--mu)" }}>{sub}</span> : null}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Sparkline — tiny line chart with shaded fill underneath.
// ──────────────────────────────────────────────────────────────────────────────

export function Sparkline({
  data,
  color = "#3B82F6",
  height = 32,
  width = 120,
}: {
  data: number[];
  color?: string;
  height?: number;
  width?: number;
}) {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`)
    .join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`0,${height} ${pts} ${width},${height}`} fill={color} opacity={0.08} />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Toggle — pill switch.
// ──────────────────────────────────────────────────────────────────────────────

export function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--tx2)" }}
    >
      <div
        style={{
          width: 32,
          height: 18,
          borderRadius: 99,
          background: on ? "var(--accent)" : "var(--b3)",
          position: "relative",
          transition: "background 150ms",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 150ms",
          }}
        />
      </div>
      {label}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Empty state — rosette + eyebrow + headline + body + action.
// ──────────────────────────────────────────────────────────────────────────────

export function Empty({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "64px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      {icon ?? <Rosette size={56} stroke="var(--accent)" opacity={0.6} />}
      <div className="eyebrow" style={{ marginTop: 8 }}>Empty state</div>
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          maxWidth: 380,
        }}
      >
        {title}
      </div>
      {body ? <div style={{ color: "var(--tx2)", fontSize: 13, maxWidth: 360 }}>{body}</div> : null}
      {action ? <div style={{ marginTop: 8 }}>{action}</div> : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PageHead — shared page header (eyebrow + title + subtitle + actions).
// Lifted from home.jsx so other pages can reuse the same hero treatment.
// ──────────────────────────────────────────────────────────────────────────────

export function PageHead({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div
      className="bg-red-glow"
      style={{
        padding: "28px 32px 24px",
        borderBottom: "1px solid var(--b1)",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 24, position: "relative", zIndex: 1 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>{eyebrow}</div>
          <h1 style={{ fontSize: 32, lineHeight: 1.1 }}>{title}</h1>
          {subtitle ? (
            <div style={{ color: "var(--tx2)", marginTop: 8, fontSize: 14, maxWidth: 720 }}>{subtitle}</div>
          ) : null}
        </div>
        {actions ? <div className="page-head-actions" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{actions}</div> : null}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Image-generation failure reason
//
// When image generation fails the operator used to see only a blank
// gradient — a safety block, a quota outage and a provider fault were
// indistinguishable, and all three got mistaken for a billing problem.
// content-service now returns a machine-readable reason alongside the
// version/post row (additive fields, so older rows simply don't have it).
//
// EVERYTHING here is defensive: no fields ⇒ `imageFailure()` returns null
// and the caller renders exactly what it rendered before. Never show an
// empty notice box.
// ──────────────────────────────────────────────────────────────────────────────

/** Known kinds. Anything else falls back to the generic provider wording. */
export type ImageErrorKind =
  | "safety_blocked"
  | "quota_exhausted"
  | "provider_error"
  | "no_engine_available"
  | (string & {});

export interface ImageFailure {
  kind: ImageErrorKind;
  /** Human message from the backend, when it sent one. */
  message?: string;
}

/**
 * Pull the failure off any version/variant/post-shaped object.
 *
 * Tolerates: fields absent entirely (older rows / backend not yet shipped),
 * either field present without the other, and null/empty strings. Returns
 * null whenever there is nothing meaningful to say — callers MUST treat
 * null as "render the old placeholder".
 */
export function imageFailure(
  src:
    | {
        image_error?: string | null;
        image_error_kind?: string | null;
        imageError?: string | null;
        imageErrorKind?: string | null;
      }
    | null
    | undefined,
): ImageFailure | null {
  if (!src) return null;
  const kind = (src.image_error_kind ?? src.imageErrorKind ?? "").trim();
  const message = (src.image_error ?? src.imageError ?? "").trim();
  // Neither field carries anything → nothing to render.
  if (!kind && !message) return null;
  return {
    kind: kind || "provider_error",
    message: message || undefined,
  };
}

function imageFailureCopy(
  kind: ImageErrorKind,
  tr: boolean,
): { title: string; hint?: string } {
  switch (kind) {
    case "safety_blocked":
      return {
        title: tr ? "Görsel güvenlik filtresine takıldı" : "Image blocked by the safety filter",
        hint: tr
          ? "Başka bir motor (Nexus AI) deneyin ya da istemi yeniden yazın."
          : "Try a different engine (Nexus AI) or rephrase the prompt.",
      };
    case "quota_exhausted":
      return {
        title: tr ? "Görsel kotası doldu" : "Image quota exhausted",
        hint: tr
          ? "Görsel sağlayıcının kotası tükendi — bu bir ödeme sorunu değil. Biraz sonra tekrar deneyin."
          : "The image provider is out of quota — this is not a billing problem. Try again shortly.",
      };
    // The image router had no engine left to try (every configured engine
    // disabled/unreachable, or the brand's roster resolved empty). Retrying
    // changes nothing until an engine is restored, so this must NOT reuse
    // the provider_error copy that tells the operator to try again.
    case "no_engine_available":
      return {
        title: tr ? "Uygun görsel motoru yok" : "No image engine available",
        hint: tr
          ? "Şu anda yapılandırılmış bir görsel motoru bulunmuyor. Yeniden denemek sonucu değiştirmez — destek ekibine bildirin."
          : "No image engine is currently available. Retrying will not help — please report this to support.",
      };
    case "provider_error":
    default:
      return {
        title: tr ? "Görsel üretilemedi" : "Image could not be generated",
        hint: tr
          ? "Görsel sağlayıcı yanıt vermedi. Yeniden üretmeyi deneyin."
          : "The image provider did not respond. Try regenerating.",
      };
  }
}

/**
 * Short inline notice explaining why an image is missing.
 *
 * Render it INSTEAD of the blank placeholder, never in addition to a real
 * image. Pass `overlay` when it sits on top of the lane's gradient.
 */
export function ImageFailureNotice({
  failure,
  tr,
  overlay = false,
}: {
  failure: ImageFailure | null | undefined;
  tr: boolean;
  overlay?: boolean;
}) {
  if (!failure) return null;
  const copy = imageFailureCopy(failure.kind, tr);
  const isSafety = failure.kind === "safety_blocked";
  const tone = isSafety ? "var(--warning)" : "var(--error)";
  return (
    <div
      role="status"
      style={{
        ...(overlay
          ? {
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: 16,
              background: "rgba(10,10,10,0.72)",
              zIndex: 2,
            }
          : {
              padding: "8px 10px",
              borderRadius: 6,
              border: `1px solid ${tone}`,
              background: isSafety
                ? "rgba(245,158,11,0.08)"
                : "rgba(239,68,68,0.08)",
            }),
        gap: 4,
        textAlign: overlay ? "center" : "left",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: tone, lineHeight: 1.4 }}>
        {copy.title}
      </div>
      {copy.hint ? (
        <div style={{ fontSize: 11, color: "var(--tx2)", lineHeight: 1.5 }}>
          {copy.hint}
        </div>
      ) : null}
      {/* Backend's own words, when it sent any — kept last and muted so the
          actionable line stays the headline. */}
      {failure.message ? (
        <div
          className="mono"
          style={{ fontSize: 10, color: "var(--mu)", lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {failure.message}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function initialsOf(name?: string): string {
  if (!name) return "?";
  return name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export function hexToRgba(hex: string, alpha: number): string {
  const m = hex.replace("#", "").match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return `rgba(224,45,28,${alpha})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
